<template>
  <div class="token-store-fullscreen">
    <div class="store-shell">
      <div class="store-hero">
        <div>
          <span class="hero-kicker">Token Store</span>
          <h2>{{ $t('settingsModal.tokenStore') }}</h2>
          <p>{{ $t('settingsModal.tokenStoreTip') }}</p>
        </div>
        <button class="close-btn" @click="$emit('close')">×</button>
      </div>

      <div v-if="tokenStoreLoading" class="token-store-loading">{{ $t('settingsModal.loadingStore') }}</div>
      <div v-else class="store-content">
        <section class="token-orders-panel">
          <div class="token-orders-header">
            <h3>🧾 {{ $t('settingsModal.orderManagement') }}</h3>
            <button class="mini-link-btn" :disabled="tokenStoreOrdersLoading" @click="refreshTokenStoreOrders">
              {{ tokenStoreOrdersLoading ? $t('settingsModal.processing') : $t('settingsModal.refreshOrders') }}
            </button>
          </div>
          <p v-if="paidTokenStoreOrders.length > 0" class="token-orders-help">使用出现问题，请添加作者微信：Auj625197595</p>
          <div v-if="paidTokenStoreOrders.length === 0" class="token-orders-empty">{{ $t('settingsModal.noOrders') }}</div>
          <div v-else class="token-order-list">
            <div v-for="order in paidTokenStoreOrders" :key="order.orderNo" class="token-order-item">
              <div class="order-main">
                <strong>{{ order.modelName || order.modelId }}</strong>
                <span>{{ order.orderNo }} · {{ order.amount }}元 · {{ formatOrderStatus(order.status) }}</span>
                <div v-if="orderHasBalance(order)" class="order-quota">
                  <div class="order-quota-label">
                    <span>{{ formatOrderBalance(order) }}</span>
                    <span>{{ orderBalancePercent(order) }}%</span>
                  </div>
                  <div class="order-quota-track">
                    <i :style="{ width: `${orderBalancePercent(order)}%` }"></i>
                  </div>
                </div>
              </div>
              <div class="order-actions">
                <button class="mini-link-btn" @click="refreshTokenStoreOrder(order)">{{ $t('settingsModal.refresh') }}</button>
                <button v-if="order.modelConfig && order.modelConfig.apiKey" class="mini-link-btn" @click="viewTokenStoreConfig(order)">{{ $t('settingsModal.viewConfig') }}</button>
                <button v-if="order.modelConfig && !isOrderModelAdded(order)" class="mini-primary-btn" @click="useTokenStoreOrder(order)">{{ $t('settingsModal.useNow') }}</button>
                <span v-else-if="order.modelConfig" class="order-added-badge">{{ $t('settingsModal.added') }}</span>
              </div>
            </div>
          </div>
        </section>

        <section v-for="category in modelCategories" :key="'store-'+category.id" class="token-store-category">
          <h3>{{ category.icon }} {{ category.name }}</h3>
          <div class="token-store-grid">
            <div v-for="item in getStoreItemsByType(category.id)" :key="item.id" class="token-store-card">
              <div class="store-card-glow"></div>
              <div class="store-card-main">
                <div class="store-avatar">
                  <img :src="storeModelIcon(item)" :alt="`${item.provider || item.name} 图标`">
                </div>
                <div class="store-title-block">
                  <span class="store-provider">{{ item.provider }}</span>
                  <strong>{{ item.name }}</strong>
                  <span class="store-model-id">{{ item.model }}</span>
                </div>
              </div>
              <div v-if="item.inputPrice || item.outputPrice" class="store-price-grid">
                <div class="store-price-box">
                  <span>{{ $t('settingsModal.inputPrice') }}</span>
                  <strong>💰{{ item.inputPrice }}</strong>
                  <em>/ {{ item.priceUnit || '1M Tokens' }}</em>
                </div>
                <div class="store-price-box accent">
                  <span>{{ $t('settingsModal.outputPrice') }}</span>
                  <strong>💰{{ item.outputPrice }}</strong>
                  <em>/ {{ item.priceUnit || '1M Tokens' }}</em>
                </div>
              </div>
              <div v-else-if="item.countPrice" class="store-price-grid single">
                <div class="store-price-box">
                  <span>{{ $t('settingsModal.countPrice') }}</span>
                  <strong>💰{{ item.countPrice }}</strong>
                  <em>/ {{ item.priceUnit || $t('settingsModal.perUse') }}</em>
                </div>
              </div>
              <div v-else class="store-price-simple">{{ item.priceText }}</div>
              <div class="store-actions">
                <a :href="item.officialUrl" target="_blank" @click.stop>{{ $t('settingsModal.officialSite') }}</a>
                <button class="store-buy-btn" :disabled="tokenStoreBuyingId === item.id" @click="openTokenStorePurchase(item)">
                  {{ tokenStoreBuyingId === item.id ? $t('settingsModal.processing') : $t('settingsModal.buy') }}
                </button>
              </div>
            </div>
            <div v-if="getStoreItemsByType(category.id).length === 0" class="empty-state-compact">
              <span>{{ $t('settingsModal.emptyConfig') }}</span>
            </div>
          </div>
        </section>
      </div>
    </div>

    <div v-if="tokenStorePurchaseItem" class="token-dialog-overlay" @click.self="closeTokenStorePurchase">
      <div class="token-dialog">
        <div class="dialog-header">
          <div>
            <span>{{ tokenStorePurchaseItem.provider }}</span>
            <h3>{{ tokenStorePurchaseItem.name }}</h3>
          </div>
          <button class="close-dialog-btn" @click="closeTokenStorePurchase">×</button>
        </div>
        <div class="purchase-price-summary">
          <div v-if="tokenStorePurchaseItem.inputPrice">
            <span>{{ $t('settingsModal.inputPrice') }}</span>
            <strong>💰{{ tokenStorePurchaseItem.inputPrice }}</strong>
            <em>/ {{ tokenStorePurchaseItem.priceUnit || '1M Tokens' }}</em>
          </div>
          <div v-if="tokenStorePurchaseItem.outputPrice">
            <span>{{ $t('settingsModal.outputPrice') }}</span>
            <strong>💰{{ tokenStorePurchaseItem.outputPrice }}</strong>
            <em>/ {{ tokenStorePurchaseItem.priceUnit || '1M Tokens' }}</em>
          </div>
        </div>
        <label class="purchase-amount-label">{{ $t('settingsModal.purchaseAmount') }}</label>
        <input type="number" min="1" step="1" v-model.number="tokenStorePurchaseAmount" class="purchase-amount-input" :placeholder="$t('settingsModal.amountPlaceholder')">
        <button class="primary-block-btn" :disabled="tokenStoreBuyingId === tokenStorePurchaseItem.id" @click="buyTokenStoreModel(tokenStorePurchaseItem)">
          {{ tokenStoreBuyingId === tokenStorePurchaseItem.id ? $t('settingsModal.processing') : $t('settingsModal.confirmPay') }}
        </button>
      </div>
    </div>

    <div v-if="tokenStoreConfigOrder" class="token-dialog-overlay" @click.self="closeTokenStoreConfig">
      <div class="token-dialog config-view-dialog">
        <div class="dialog-header">
          <div>
            <span>{{ tokenStoreConfigOrder.modelConfig.provider || 'token-store' }}</span>
            <h3>{{ $t('settingsModal.configDetail') }}</h3>
          </div>
          <button class="close-dialog-btn" @click="closeTokenStoreConfig">×</button>
        </div>
        <div class="config-detail-list">
          <label>{{ $t('settingsModal.endpoint') }}</label>
          <div>{{ tokenStoreConfigOrder.modelConfig.endpoint }}</div>
          <label>{{ $t('settingsModal.configName') }}</label>
          <div>{{ tokenStoreConfigOrder.modelConfig.name }}</div>
          <label>{{ $t('settingsModal.apiKey') }}</label>
          <div class="config-key">{{ tokenStoreConfigOrder.modelConfig.apiKey }}</div>
        </div>
        <button class="primary-block-btn" @click="copyTokenStoreConfigAll(tokenStoreConfigOrder)">
          {{ $t('settingsModal.copyAll') }}
        </button>
      </div>
    </div>

    <div v-if="successDialog.visible" class="token-dialog-overlay success-overlay">
      <div class="success-dialog">
        <div class="success-icon">✓</div>
        <h3>{{ $t('settingsModal.purchaseSuccessTitle') }}</h3>
        <p>{{ $t('settingsModal.purchaseSuccessDefaultModel', { name: successDialog.modelName }) }}</p>
        <button class="chat-cta-btn" @click="goToChat">{{ $t('settingsModal.goChatNow') }}</button>
        <button class="success-secondary-btn" @click="successDialog.visible = false">{{ $t('settingsModal.stayInStore') }}</button>
      </div>
    </div>
  </div>
</template>

<script>
import { store, mutations } from '@/store';
import { showToast } from '@/utils/toast';

const TOKEN_STORE_API_BASE = 'https://wm.ktllq.cn/Token';
const TOKEN_STORE_ORDERS_KEY = 'aihelp_token_store_orders';

export default {
  name: 'TokenStoreFullscreen',
  data() {
    return {
      tokenStoreLoading: false,
      tokenStoreBuyingId: '',
      tokenStoreCatalog: [],
      tokenStoreAmounts: {},
      tokenStorePurchaseItem: null,
      tokenStorePurchaseAmount: 5,
      tokenStorePollTimer: null,
      tokenStoreOrders: [],
      tokenStoreOrdersLoading: false,
      tokenStoreConfigOrder: null,
      successDialog: {
        visible: false,
        modelName: ''
      }
    };
  },
  computed: {
    modelCategories() {
      return [
        { id: 'text', name: this.$t('settingsModal.modelCategories.text'), icon: '💬' },
        { id: 't2i', name: this.$t('settingsModal.modelCategories.t2i'), icon: '🎨' },
        { id: 'i2i', name: this.$t('settingsModal.modelCategories.i2i'), icon: '🖼️' }
      ];
    },
    paidTokenStoreOrders() {
      return this.tokenStoreOrders.filter(order => ['paid', 'issued'].includes(order.status));
    }
  },
  async mounted() {
    this.loadTokenStoreOrders();
    this.loadTokenStoreCatalog();
    this.tokenStoreOrdersLoading = true;
    await this.syncCloudTokenStoreOrders();
    this.tokenStoreOrdersLoading = false;
    this.refreshTokenStoreOrders();
  },
  beforeDestroy() {
    this.stopTokenStorePolling();
  },
  methods: {
    showToast(message, type = 'success', duration = 2200) {
      showToast(message, type, duration);
    },
    showPurchaseSuccessDialog(modelConfig) {
      this.successDialog.modelName = modelConfig.name || modelConfig.model || '';
      this.successDialog.visible = true;
    },
    goToChat() {
      this.successDialog.visible = false;
      this.$emit('close');
    },
    getStoreItemsByType(type) {
      return this.tokenStoreCatalog.filter(item => item.type === type || item.category === type);
    },
    storeModelIcon(item) {
      const value = [item && item.provider, item && item.name, item && item.model]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      const iconMatchers = [
        ['deepseek', '/model-deepseek.png'],
        ['claude', '/model-claude.png'],
        ['anthropic', '/model-claude.png'],
        ['gemini', '/model-gemini.png'],
        ['grok', '/model-grok.png'],
        ['xai', '/model-grok.png'],
        ['minimax', '/model-minimax.png'],
        ['豆包', '/model-doubao.png'],
        ['doubao', '/model-doubao.png'],
        ['火山引擎', '/model-doubao.png'],
        ['qwen', '/model-qwen.png'],
        ['通义', '/model-qwen.png'],
        ['千问', '/model-qwen.png'],
        ['dashscope', '/model-qwen.png'],
        ['glm', '/model-zhipu.png'],
        ['智谱', '/model-zhipu.png'],
        ['bigmodel', '/model-zhipu.png'],
        ['gpt', '/model-gpt.png'],
        ['openai', '/model-gpt.png']
      ];
      const match = iconMatchers.find(([keyword]) => value.includes(keyword));
      return match ? match[1] : '/model-default.png';
    },
    loadTokenStoreOrders() {
      try {
        const saved = localStorage.getItem(TOKEN_STORE_ORDERS_KEY);
        this.tokenStoreOrders = saved ? JSON.parse(saved) : [];
      } catch (e) {
        this.tokenStoreOrders = [];
      }
    },
    saveTokenStoreOrders() {
      localStorage.setItem(TOKEN_STORE_ORDERS_KEY, JSON.stringify(this.tokenStoreOrders));
    },
    upsertTokenStoreOrder(order) {
      const index = this.tokenStoreOrders.findIndex(item => item.orderNo === order.orderNo);
      if (index === -1) {
        this.tokenStoreOrders.unshift(order);
      } else {
        this.$set(this.tokenStoreOrders, index, { ...this.tokenStoreOrders[index], ...order });
      }
      this.saveTokenStoreOrders();
    },
    formatOrderStatus(status) {
      const map = {
        pending: this.$t('settingsModal.orderPending'),
        paid: this.$t('settingsModal.orderPaid'),
        issued: this.$t('settingsModal.orderIssued'),
        failed: this.$t('settingsModal.orderFailed')
      };
      return map[status] || status || this.$t('settingsModal.orderPending');
    },
    isOrderModelAdded(order) {
      return store.settings.models.some(model => model.tokenStore && model.tokenStore.orderNo === order.orderNo);
    },
    orderTokenId(order) {
      return order.modelConfig && order.modelConfig.tokenStore && order.modelConfig.tokenStore.tokenId;
    },
    orderQuotaTotal(order) {
      return Number(order.quota || (order.modelConfig && order.modelConfig.tokenStore && order.modelConfig.tokenStore.quota) || 0);
    },
    orderHasBalance(order) {
      return !!this.orderTokenId(order) && (order.remainAmount !== undefined || order.remainQuota !== undefined);
    },
    formatOrderBalance(order) {
      if (!this.orderHasBalance(order)) return this.$t('settingsModal.balanceLoading');
      if (order.remainAmount !== null && order.remainAmount !== undefined) {
        return this.$t('settingsModal.balanceAmount', { amount: order.remainAmount });
      }
      const amount = ((Number(order.remainQuota) || 0) / 500000).toFixed(2);
      return this.$t('settingsModal.balanceAmount', { amount });
    },
    orderBalancePercent(order) {
      let remaining;
      let total;
      if (order.remainAmount !== null && order.remainAmount !== undefined && Number(order.amount) > 0) {
        remaining = Number(order.remainAmount);
        total = Number(order.amount);
      } else {
        remaining = Number(order.remainQuota);
        total = this.orderQuotaTotal(order);
      }
      if (!Number.isFinite(remaining) || !Number.isFinite(total) || total <= 0) return 0;
      return Math.round(Math.max(0, Math.min(100, remaining / total * 100)));
    },
    async refreshOrderBalance(order) {
      const tokenId = order.modelConfig && order.modelConfig.tokenStore && order.modelConfig.tokenStore.tokenId;
      if (!tokenId) return;
      try {
        const response = await fetch(`${TOKEN_STORE_API_BASE}/balance?tokenId=${encodeURIComponent(tokenId)}&token=${encodeURIComponent(store.token || '')}`);
        const result = await response.json();
        if (result.code !== 0) throw new Error(result.msg || 'balance error');
        this.upsertTokenStoreOrder({
          ...order,
          remainQuota: result.data.remainQuota,
          usedQuota: result.data.usedQuota,
          remainAmount: result.data.remainAmount
        });
      } catch (e) {
        // Keep the last known quota visible if a refresh temporarily fails.
      }
    },
    async loadTokenStoreCatalog() {
      this.tokenStoreLoading = true;
      try {
        const response = await fetch(`${TOKEN_STORE_API_BASE}/catalog`);
        const result = await response.json();
        if (result.code !== 0) {
          throw new Error(result.msg || this.$t('settingsModal.loadStoreFailed'));
        }
        this.tokenStoreCatalog = result.data.items || [];
        this.tokenStoreCatalog.forEach(item => {
          if (!this.tokenStoreAmounts[item.id]) {
            this.$set(this.tokenStoreAmounts, item.id,  5);
          }
        });
      } catch (e) {
        this.showToast(this.$t('settingsModal.loadStoreFailed') + ': ' + e.message, 'error');
      } finally {
        this.tokenStoreLoading = false;
      }
    },
    openTokenStorePurchase(item) {

      console.log("tokenStoreAmounts", this.tokenStoreAmounts);
      this.tokenStorePurchaseItem = item;
      this.tokenStorePurchaseAmount = this.tokenStoreAmounts[item.id] || 5;
    },
    closeTokenStorePurchase() {
      if (!this.tokenStoreBuyingId) {
        this.tokenStorePurchaseItem = null;
      }
    },
    async buyTokenStoreModel(item) {
      const amount = Number(this.tokenStorePurchaseAmount || 0);
      this.$set(this.tokenStoreAmounts, item.id, amount);
      this.tokenStoreBuyingId = item.id;
      try {
        const response = await fetch(`${TOKEN_STORE_API_BASE}/createOrder`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            token: store.token || '',
            modelId: item.id,
            amount
          })
        });
        const result = await response.json();
        if (result.code !== 0) {
          throw new Error(result.msg || this.$t('settingsModal.createOrderFailed'));
        }
        this.upsertTokenStoreOrder({
          orderNo: result.data.orderNo,
          modelId: item.id,
          modelName: item.name,
          amount,
          status: 'pending',
          createdAt: new Date().toISOString()
        });
        window.open(result.data.payUrl, '_blank');
        this.tokenStorePurchaseItem = null;
        this.pollTokenStoreOrder(result.data.orderNo, item);
      } catch (e) {
        this.showToast(this.$t('settingsModal.createOrderFailed') + ': ' + e.message, 'error');
        this.tokenStoreBuyingId = '';
      }
    },
    pollTokenStoreOrder(orderNo, item) {
      this.stopTokenStorePolling();
      let pollCount = 0;
      this.tokenStorePollTimer = setInterval(async () => {
        pollCount += 1;
        try {
          const response = await fetch(`${TOKEN_STORE_API_BASE}/orderStatus?orderNo=${encodeURIComponent(orderNo)}&token=${encodeURIComponent(store.token || '')}`);
          const result = await response.json();
          if (result.code !== 0) {
            throw new Error(result.msg || this.$t('settingsModal.queryOrderFailed'));
          }
          const status = result.data.status;
          this.upsertTokenStoreOrder({
            orderNo,
            modelId: item.id,
            modelName: item.name,
            amount: result.data.amount,
            quota: result.data.quota,
            status,
            errorMsg: result.data.errorMsg || '',
            modelConfig: result.data.modelConfig || undefined
          });
          if (result.data.modelConfig) {
            await this.refreshOrderBalance({
              ...this.tokenStoreOrders.find(order => order.orderNo === orderNo),
              modelConfig: result.data.modelConfig,
              quota: result.data.quota
            });
          }
          if (status === 'issued' && result.data.modelConfig) {
            this.stopTokenStorePolling();
            this.tokenStoreBuyingId = '';
            if (!this.isOrderModelAdded({ orderNo })) {
              const savedModel = mutations.addModel(result.data.modelConfig);
              mutations.setActiveModelByType(savedModel.id, result.data.modelConfig.type || item.type || 'text');
              this.$emit('model-added');
            }
            this.showPurchaseSuccessDialog(result.data.modelConfig);
            this.showToast(this.$t('settingsModal.purchaseSuccessTip'), 'success', 3200);
          } else if (status === 'failed') {
            this.stopTokenStorePolling();
            this.tokenStoreBuyingId = '';
            this.showToast(this.$t('settingsModal.purchaseFailed') + (result.data.errorMsg ? ': ' + result.data.errorMsg : ''), 'error');
          } else if (pollCount > 120) {
            this.stopTokenStorePolling();
            this.tokenStoreBuyingId = '';
            this.showToast(this.$t('settingsModal.purchaseTimeout'), 'warning');
          }
        } catch (e) {
          if (pollCount > 5) {
            this.stopTokenStorePolling();
            this.tokenStoreBuyingId = '';
            this.showToast(this.$t('settingsModal.queryOrderFailed') + ': ' + e.message, 'error');
          }
        }
      }, 3000);
    },
    stopTokenStorePolling() {
      if (this.tokenStorePollTimer) {
        clearInterval(this.tokenStorePollTimer);
        this.tokenStorePollTimer = null;
      }
    },
    async refreshTokenStoreOrder(order) {
      try {
        const response = await fetch(`${TOKEN_STORE_API_BASE}/orderStatus?orderNo=${encodeURIComponent(order.orderNo)}&token=${encodeURIComponent(store.token || '')}`);
        const result = await response.json();
        if (result.code !== 0) {
          throw new Error(result.msg || this.$t('settingsModal.queryOrderFailed'));
        }
        this.upsertTokenStoreOrder({
          ...order,
          status: result.data.status,
          amount: result.data.amount || order.amount,
          quota: result.data.quota || order.quota,
          errorMsg: result.data.errorMsg || '',
          modelConfig: result.data.modelConfig || order.modelConfig
        });
        const updatedOrder = this.tokenStoreOrders.find(item => item.orderNo === order.orderNo) || order;
        await this.refreshOrderBalance(updatedOrder);
      } catch (e) {
        this.showToast(this.$t('settingsModal.queryOrderFailed') + ': ' + e.message, 'error');
      }
    },
    // 订单以服务端为准(按登录 token 归属):本地缓存被清理或换设备后从云端补回。
    async syncCloudTokenStoreOrders() {
      if (!store.token) return;
      try {
        const response = await fetch(`${TOKEN_STORE_API_BASE}/orders?token=${encodeURIComponent(store.token)}`);
        const result = await response.json();
        if (result.code !== 0) return;
        // 服务端按新到旧返回,upsert 对新订单是 unshift,倒序插入以保持新订单在前
        (result.data.orders || []).slice().reverse().forEach(order => {
          const local = this.tokenStoreOrders.find(item => item.orderNo === order.orderNo);
          this.upsertTokenStoreOrder({
            ...order,
            modelConfig: order.modelConfig || (local && local.modelConfig) || undefined
          });
        });
      } catch (e) {
        // 云端不可用时沿用本地缓存
      }
    },
    async refreshTokenStoreOrders() {
      this.tokenStoreOrdersLoading = true;
      try {
        await Promise.all(this.tokenStoreOrders.map(order => this.refreshTokenStoreOrder(order)));
      } finally {
        this.tokenStoreOrdersLoading = false;
      }
    },
    viewTokenStoreConfig(order) {
      if (!order.modelConfig || !order.modelConfig.apiKey) return;
      this.tokenStoreConfigOrder = order;
    },
    closeTokenStoreConfig() {
      this.tokenStoreConfigOrder = null;
    },
    async copyText(text) {
      try {
        await navigator.clipboard.writeText(text);
      } catch (e) {
        const input = document.createElement('textarea');
        input.value = text;
        document.body.appendChild(input);
        input.select();
        document.execCommand('copy');
        document.body.removeChild(input);
      }
    },
    async copyTokenStoreConfigAll(order) {
      const config = order.modelConfig;
      if (!config || !config.apiKey) return;
      const text = [
        `${this.$t('settingsModal.endpoint')}：${config.endpoint}`,
        `${this.$t('settingsModal.configName')}：${config.name}`,
        `${this.$t('settingsModal.apiKey')}：${config.apiKey}`
      ].join('\n');
      await this.copyText(text);
      this.showToast(this.$t('settingsModal.copySuccess'), 'success');
    },
    async useTokenStoreOrder(order) {
      let currentOrder = order;
      if (!currentOrder.modelConfig) {
        await this.refreshTokenStoreOrder(order);
        currentOrder = this.tokenStoreOrders.find(item => item.orderNo === order.orderNo) || order;
      }
      if (!currentOrder.modelConfig) {
        this.showToast(this.$t('settingsModal.orderNotIssued'), 'warning');
        return;
      }
      if (this.isOrderModelAdded(currentOrder)) {
        this.showToast(this.$t('settingsModal.modelAlreadyAdded'), 'warning');
        return;
      }
      const savedModel = mutations.addModel(currentOrder.modelConfig);
      mutations.setActiveModelByType(savedModel.id, currentOrder.modelConfig.type || 'text');
      this.$emit('model-added');
      this.showPurchaseSuccessDialog(currentOrder.modelConfig);
      this.showToast(this.$t('settingsModal.useSuccess'), 'success');
    }
  }
};
</script>

<style scoped>
.token-store-fullscreen {
  position: fixed;
  inset: 0;
  z-index: 2500;
  background:
    radial-gradient(circle at 18% 8%, rgba(47, 84, 235, 0.24), transparent 30%),
    radial-gradient(circle at 88% 18%, rgba(124, 58, 237, 0.22), transparent 28%),
    #f4f7fc;
  color: #1e293b;
  overflow-y: auto;
  padding: 28px;
}

.store-shell {
  max-width: 1180px;
  margin: 0 auto;
}

.store-hero {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 20px;
  background: rgba(255, 255, 255, 0.82);
  border: 1px solid rgba(47, 84, 235, 0.14);
  border-radius: 24px;
  padding: 24px;
  box-shadow: 0 18px 50px rgba(15, 23, 42, 0.10);
  backdrop-filter: blur(14px);
}

.hero-kicker {
  color: #2f54eb;
  font-size: 12px;
  font-weight: 900;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.store-hero h2 {
  margin: 6px 0 8px;
  font-size: 30px;
}

.store-hero p {
  margin: 0;
  color: #64748b;
  line-height: 1.6;
}

.close-btn,
.close-dialog-btn {
  align-items: center;
  border: none;
  border-radius: 999px;
  background: #fff;
  color: #64748b;
  cursor: pointer;
  display: inline-flex;
  flex: 0 0 38px;
  font-size: 24px;
  height: 38px;
  justify-content: center;
  line-height: 1;
  min-width: 38px;
  padding: 0;
  width: 38px;
  box-shadow: 0 10px 24px rgba(15, 23, 42, 0.10);
}

.store-content {
  display: flex;
  flex-direction: column;
  gap: 18px;
  margin-top: 18px;
}

.token-store-loading,
.token-orders-panel,
.token-store-category {
  background: rgba(255, 255, 255, 0.86);
  border: 1px solid rgba(47, 84, 235, 0.12);
  border-radius: 20px;
  padding: 16px;
  box-shadow: 0 12px 34px rgba(15, 23, 42, 0.08);
}

.token-orders-header,
.token-order-item,
.order-actions,
.store-card-main,
.store-actions {
  display: flex;
  align-items: center;
}

.token-orders-header,
.token-order-item,
.store-actions {
  justify-content: space-between;
  gap: 12px;
}

.token-orders-header h3,
.token-store-category h3 {
  margin: 0 0 12px;
  font-size: 16px;
}

.token-orders-header h3 {
  margin-bottom: 0;
}

.token-orders-empty,
.empty-state-compact {
  color: #64748b;
  font-size: 13px;
}

.token-orders-help {
  margin: 0 0 12px;
  color: #64748b;
  font-size: 12px;
}

.token-order-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 12px;
}

.token-order-item {
  background: #fff;
  border: 1px solid rgba(15, 23, 42, 0.08);
  border-radius: 14px;
  padding: 10px 12px;
}

.order-main {
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
}

.order-main span {
  color: #64748b;
  font-size: 12px;
  word-break: break-all;
}

.order-actions {
  flex-wrap: wrap;
  justify-content: flex-end;
}

.mini-link-btn,
.mini-primary-btn {
  border-radius: 999px;
  padding: 6px 11px;
  font-size: 12px;
  font-weight: 800;
  cursor: pointer;
}

.mini-link-btn {
  border: 1px solid rgba(47, 84, 235, 0.22);
  background: transparent;
  color: #2f54eb;
}

.mini-primary-btn,
.primary-block-btn,
.store-buy-btn {
  border: none;
  background: linear-gradient(135deg, #2f54eb, #7c3aed);
  color: #fff;
}

.order-added-badge {
  color: #2f54eb;
  font-size: 12px;
  font-weight: 900;
}

.token-store-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 12px;
}

.token-store-card {
  position: relative;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  gap: 10px;
  background: linear-gradient(180deg, rgba(255,255,255,0.98), rgba(248,250,255,0.98));
  border: 1px solid rgba(47, 84, 235, 0.12);
  border-radius: 16px;
  padding: 12px;
  box-shadow: 0 10px 26px rgba(15, 23, 42, 0.07);
}

.store-card-glow {
  position: absolute;
  width: 95px;
  height: 95px;
  right: -36px;
  top: -36px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(47, 84, 235, 0.20), transparent 65%);
}

.store-card-main {
  gap: 9px;
  position: relative;
  z-index: 1;
}

.store-avatar {
  box-sizing: border-box;
  width: 36px;
  height: 36px;
  border-radius: 12px;
  padding: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: #fff;
  font-weight: 900;
  background: #f1f5f9;
}

.order-quota {
  width: min(100%, 320px);
  margin-top: 5px;
}

.order-quota-label {
  display: flex;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 5px;
  color: #64748b;
  font-size: 11px;
}

.order-quota-track {
  height: 6px;
  overflow: hidden;
  border-radius: 999px;
  background: #e2e8f0;
}

.order-quota-track i {
  display: block;
  height: 100%;
  border-radius: inherit;
  background: linear-gradient(90deg, #2f54eb, #7c3aed);
  transition: width 0.25s ease;
}

.store-avatar img {
  width: 100%;
  height: 100%;
  display: block;
  border-radius: inherit;
  object-fit: contain;
}

.store-title-block {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.store-provider {
  color: #2f54eb;
  font-size: 11px;
  font-weight: 900;
}

.store-title-block strong {
  font-size: 14px;
}

.store-model-id {
  color: #64748b;
  font-size: 11px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.store-price-grid,
.purchase-price-summary {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 6px;
}

.store-price-grid.single {
  grid-template-columns: 1fr;
}

.store-price-box,
.purchase-price-summary div,
.store-price-simple {
  border-radius: 11px;
  padding: 8px;
  background: rgba(47, 84, 235, 0.07);
  border: 1px solid rgba(47, 84, 235, 0.10);
}

.store-price-box {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.store-price-box.accent,
.purchase-price-summary div:nth-child(2) {
  background: rgba(124, 58, 237, 0.08);
}

.store-price-box span,
.purchase-price-summary span {
  color: #64748b;
  font-size: 10px;
}

.store-price-box strong,
.purchase-price-summary strong {
  color: #1e293b;
  font-size: 13px;
}

.store-price-box em,
.purchase-price-summary em {
  color: #64748b;
  font-size: 10px;
  font-style: normal;
}

.store-price-simple {
  color: #64748b;
  font-size: 12px;
}

.store-actions a {
  color: #2f54eb;
  font-size: 12px;
  font-weight: 800;
  text-decoration: none;
}

.store-buy-btn {
  border-radius: 999px;
  cursor: pointer;
  font-size: 12px;
  font-weight: 900;
  padding: 7px 13px;
}

.token-dialog-overlay {
  position: fixed;
  inset: 0;
  z-index: 2600;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(15, 23, 42, 0.42);
}

.token-dialog {
  width: min(430px, calc(100vw - 32px));
  display: flex;
  flex-direction: column;
  gap: 14px;
  background: #fff;
  border-radius: 22px;
  padding: 20px;
  box-shadow: 0 24px 70px rgba(15, 23, 42, 0.28);
}

.success-overlay {
  background:
    radial-gradient(circle at 50% 35%, rgba(34, 197, 94, 0.22), transparent 34%),
    rgba(15, 23, 42, 0.48);
}

.success-dialog {
  width: min(430px, calc(100vw - 32px));
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
  background: rgba(255, 255, 255, 0.96);
  border: 1px solid rgba(34, 197, 94, 0.20);
  border-radius: 28px;
  padding: 30px 24px 24px;
  text-align: center;
  box-shadow: 0 28px 80px rgba(15, 23, 42, 0.30);
  animation: successPop 0.24s ease-out;
}

.success-icon {
  width: 64px;
  height: 64px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  color: #fff;
  font-size: 34px;
  font-weight: 900;
  background: linear-gradient(135deg, #16a34a, #22c55e);
  box-shadow: 0 16px 34px rgba(34, 197, 94, 0.30);
}

.success-dialog h3 {
  margin: 4px 0 0;
  color: #0f172a;
  font-size: 24px;
}

.success-dialog p {
  margin: 0;
  color: #64748b;
  line-height: 1.65;
}

.chat-cta-btn {
  width: 100%;
  border: none;
  border-radius: 16px;
  background: linear-gradient(135deg, #2f54eb, #7c3aed);
  color: #fff;
  cursor: pointer;
  font-size: 16px;
  font-weight: 900;
  padding: 13px 18px;
  box-shadow: 0 14px 30px rgba(47, 84, 235, 0.24);
}

.success-secondary-btn {
  border: none;
  background: transparent;
  color: #64748b;
  cursor: pointer;
  font-size: 13px;
  font-weight: 800;
}

@keyframes successPop {
  from {
    opacity: 0;
    transform: translateY(10px) scale(0.96);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

.dialog-header {
  display: flex;
  justify-content: space-between;
  gap: 16px;
}

.dialog-header span {
  color: #2f54eb;
  font-size: 12px;
  font-weight: 900;
}

.dialog-header h3 {
  margin: 4px 0 0;
}

.purchase-amount-label,
.config-detail-list label {
  color: #1e293b;
  font-size: 13px;
  font-weight: 900;
}

.purchase-amount-input {
  border: 1px solid rgba(15, 23, 42, 0.12);
  border-radius: 12px;
  font-size: 18px;
  font-weight: 900;
  padding: 12px;
  text-align: center;
}

.primary-block-btn {
  border-radius: 12px;
  cursor: pointer;
  font-weight: 900;
  padding: 12px;
}

.config-view-dialog {
  max-width: 460px;
}

.config-detail-list {
  display: flex;
  flex-direction: column;
  gap: 7px;
}

.config-detail-list div {
  background: rgba(47, 84, 235, 0.07);
  border: 1px solid rgba(47, 84, 235, 0.12);
  border-radius: 10px;
  color: #1e293b;
  font-size: 12px;
  line-height: 1.45;
  padding: 10px;
  word-break: break-all;
}

.config-detail-list .config-key {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", monospace;
}

@media (max-width: 640px) {
  .token-store-fullscreen {
    padding: 14px;
    padding-top:70px;
  }

  .store-hero {
    padding: 18px;
  }

  .store-hero h2 {
    font-size: 24px;
  }

  .token-order-item {
    align-items: flex-start;
    flex-direction: column;
  }
}
</style>
